import {
  InputTransactionData,
  WalletReadyState,
} from '@aptos-labs/wallet-adapter-core';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { OKXWalletName } from '../adapters/okx-adapter';
import { WalletContext } from './context-impl';
import {
  AccountInfo,
  AdapterNotDetectedWallet,
  AdapterWallet,
  AptosSignAndSubmitTransactionOutput,
  DappConfig,
  NetworkInfo,
  WalletCore,
} from './wallet-core';

export interface AptosWalletProviderProps {
  children: ReactNode;
  autoConnect?: boolean;
  dappConfig?: DappConfig;
}

const initialState: {
  account: AccountInfo | null;
  network: NetworkInfo | null;
  connected: boolean;
  wallet: AdapterWallet | null;
} = {
  connected: false,
  account: null,
  network: null,
  wallet: null,
};

export const AptosWalletAdapterProvider: FC<AptosWalletProviderProps> = ({
  children,
  autoConnect = false,
  dappConfig,
}: AptosWalletProviderProps) => {
  const didAttemptAutoConnectRef = useRef(false);

  const [{ account, connected, wallet }, setState] =
    useState(initialState);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [walletCore, setWalletCore] = useState<WalletCore>();

  const [wallets, setWallets] = useState<ReadonlyArray<AdapterWallet>>([]);
  const [notDetectedWallets, setNotDetectedWallets] = useState<
    ReadonlyArray<AdapterNotDetectedWallet>
  >([]);

  useEffect(() => {
    const walletCore = new WalletCore(
      [
        'Continue with Apple',
        'Continue with Google',
        'Petra',
        'Nightly',
        'Pontem Wallet',
        'Backpack',
        'MSafe',
        'Bitget Wallet',
        OKXWalletName,
      ],
      dappConfig,
    );
    setWalletCore(walletCore);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update initial Wallets state once WalletCore has been initialized
  useEffect(() => {
    setWallets(walletCore?.wallets ?? []);
    setNotDetectedWallets(walletCore?.notDetectedWallets ?? []);
  }, [walletCore]);

  useEffect(() => {
    // Only attempt to auto connect once per render and only if there are wallets
    if (didAttemptAutoConnectRef.current || !walletCore?.wallets.length) {
      return;
    }
    didAttemptAutoConnectRef.current = true;

    // If auto connect is not set or is false, ignore the attempt
    if (!autoConnect) {
      setIsLoading(false);
      return;
    }

    // Make sure the user has a previously connected wallet
    const walletName = localStorage.getItem('AptosWalletName');
    if (!walletName) {
      setIsLoading(false);
      return;
    }

    const selectedWallet = walletCore.wallets.find(
      e => e.name === walletName,
    ) as AdapterWallet | undefined;
    if (
      !selectedWallet ||
      selectedWallet.readyState !== WalletReadyState.Installed
    ) {
      setIsLoading(false);
      return;
    }

    if (!connected) {
      (async () => {
        try {
          await connect(walletName);
        } catch (error) {
          await Promise.reject(error);
        } finally {
          setIsLoading(false);
        }
      })();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, wallets]);

  const connect = async (walletName: string): Promise<void> => {
    try {
      setIsLoading(true);
      await walletCore?.connect(walletName);
    } catch (error: any) {
      await Promise.reject(error);
    } finally {
      setIsLoading(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    try {
      await walletCore?.disconnect();
    } catch (error) {
      await Promise.reject(error);
    }
  };

  const signAndSubmitTransaction = async (
    transaction: InputTransactionData,
  ): Promise<AptosSignAndSubmitTransactionOutput> => {
    try {
      if (!walletCore) {
        throw new Error('WalletCore is not initialized');
      }
      return await walletCore.signAndSubmitTransaction(transaction);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  // Handle the adapter's connect event
  const handleConnect = (): void => {
    setState(state => {
      return {
        ...state,
        connected: true,
        account: walletCore?.account || null,
        network: walletCore?.network || null,
        wallet: walletCore?.wallet || null,
      };
    });
  };

  // Handle the adapter's account change event
  const handleAccountChange = useCallback((): void => {
    if (!connected) return;
    if (!walletCore?.wallet) return;
    setState(state => {
      return {
        ...state,
        account: walletCore?.account || null,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // Handle the adapter's network event
  const handleNetworkChange = useCallback((): void => {
    if (!connected) return;
    if (!walletCore?.wallet) return;
    setState(state => {
      return {
        ...state,
        network: walletCore?.network || null,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  useEffect(() => {
    if (connected) {
      walletCore?.onAccountChange();
      walletCore?.onNetworkChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // Handle the adapter's disconnect event
  const handleDisconnect = (): void => {
    if (!connected) return;
    setState(state => {
      return {
        ...state,
        connected: false,
        account: walletCore?.account || null,
        network: walletCore?.network || null,
        wallet: null,
      };
    });
  };

  const handleStandardWalletsAdded = (
    standardWallet: AdapterWallet,
  ): void => {
    // Manage current wallet state by removing optional duplications
    // as new wallets are coming
    const existingWalletIndex = wallets.findIndex(
      wallet => wallet.name === standardWallet.name,
    );
    if (existingWalletIndex !== -1) {
      // If wallet exists, replace it with the new wallet
      setWallets(wallets => [
        ...wallets.slice(0, existingWalletIndex),
        standardWallet,
        ...wallets.slice(existingWalletIndex + 1),
      ]);
    } else {
      // If wallet doesn't exist, add it to the array
      setWallets(wallets => [...wallets, standardWallet]);
    }
  };

  const handleStandardNotDetectedWalletsAdded = (
    notDetectedWallet: AdapterNotDetectedWallet,
  ): void => {
    // Manage current wallet state by removing optional duplications
    // as new wallets are coming
    const existingWalletIndex = wallets.findIndex(
      wallet => wallet.name === notDetectedWallet.name,
    );
    if (existingWalletIndex !== -1) {
      // If wallet exists, replace it with the new wallet
      setNotDetectedWallets(wallets => [
        ...wallets.slice(0, existingWalletIndex),
        notDetectedWallet,
        ...wallets.slice(existingWalletIndex + 1),
      ]);
    } else {
      // If wallet doesn't exist, add it to the array
      setNotDetectedWallets(wallets => [...wallets, notDetectedWallet]);
    }
  };

  useEffect(() => {
    walletCore?.on('connect', handleConnect);
    walletCore?.on('accountChange', handleAccountChange);
    walletCore?.on('networkChange', handleNetworkChange);
    walletCore?.on('disconnect', handleDisconnect);
    walletCore?.on('standardWalletsAdded', handleStandardWalletsAdded);
    walletCore?.on(
      'standardNotDetectedWalletAdded',
      handleStandardNotDetectedWalletsAdded,
    );
    return () => {
      walletCore?.off('connect', handleConnect);
      walletCore?.off('accountChange', handleAccountChange);
      walletCore?.off('networkChange', handleNetworkChange);
      walletCore?.off('disconnect', handleDisconnect);
      walletCore?.off('standardWalletsAdded', handleStandardWalletsAdded);
      walletCore?.off(
        'standardNotDetectedWalletAdded',
        handleStandardNotDetectedWalletsAdded,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets, account]);

  return (
    <WalletContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        connect,
        disconnect,
        signAndSubmitTransaction,
        account,
        connected,
        wallet,
        wallets,
        notDetectedWallets,
        isLoading,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
